import { useState, useEffect, useRef } from "react";

// Loads an image from a permission-gated route and returns a blob URL an
// <img> can actually display.
//
// This exists because of a constraint that catches every admin-gated
// image in this codebase: authentication here is header-only
// (resolveToken reads x-auth-token, with no cookie fallback), and a
// browser never attaches custom headers to an <img src> request — it's
// a plain GET the browser issues by itself. So a bare
// <img src="/api/admin/..."> is guaranteed to 401, silently, forever.
// That is precisely what was wrong with the Competition Defaults fog
// preview: confirmed with `curl -sI` returning 401 on the same
// unauthenticated request a browser makes.
//
// The obvious shortcut is a token in the query string, and it is not
// available: that pattern was deliberately removed from this codebase
// for putting a root JWT into nginx access logs, browser history and
// onward Referer headers. Fetching here and handing the <img> an opaque
// blob: URL is the alternative, and nothing gated ever reaches a URL.
//
// Pass null for url to hold off fetching (a modal that isn't showing an
// image yet, say). Changing the url refetches — callers that need a
// manual refresh vary a cache-busting parameter, exactly as they would
// with a plain <img>.
export function useAuthedImageUrl(url, token) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const seqRef = useRef(0);
  const currentRef = useRef(null);

  useEffect(() => {
    // A TOKEN IS OPTIONAL SINCE 18 Sep 2026.
    //
    // Every caller had one until the public replay viewer, which has no
    // account by design — and `!token` held the fetch forever, so the
    // page sat on "Loading image…" and never said why.
    //
    // A null url still holds off, which is what that argument is for.
    // A null TOKEN now means an unauthenticated request, which is a
    // different thing and used to be indistinguishable from it.
    if (!url) return;
    const seq = ++seqRef.current;
    let cancelled = false;
    fetch(url, token ? { headers: { "x-auth-token": token } } : undefined)
      .then((res) => {
        if (!res.ok) throw new Error("image unavailable");
        return res.blob();
      })
      .then((blob) => {
        // A response that arrived after a newer one is dropped rather
        // than displayed. This matters wherever the url changes faster
        // than the network answers — replay playback can step several
        // frames a second — since a slower earlier request landing last
        // would show an older image than the current position calls for.
        if (cancelled || seq !== seqRef.current) return;
        const next = URL.createObjectURL(blob);
        if (currentRef.current) URL.revokeObjectURL(currentRef.current);
        currentRef.current = next;
        setSrc(next);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled && seq === seqRef.current) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, token]);

  // Revoked on unmount as well as on replacement above — without this a
  // long replay leaks one object URL per step.
  useEffect(
    () => () => {
      if (currentRef.current) URL.revokeObjectURL(currentRef.current);
    },
    []
  );

  return { src, failed };
}
